import { toast } from "sonner";
import { API_ENDPOINTS, HTTP_METHODS } from "../config/api";
import { BaseApiService } from "./baseApi";

// Represents a single combined tracking record from the API response (flat array)
export interface RawTrackingRecord {
  heatNo: string;
  grnDetailId: number;
  grnReceivedQuantity: number;
  grnQualityCheck: string | null;
  supplierHeatNo: string;
  tcNo: string;
  tcDate: string | null;
  grnReceivedDate: string;
  grnId: number;
  grnNumber: string;
  grnReceiveDate: string;
  grnBranchId: number;
  grnBranchName: string;
  rawItemId: number;
  rawItemName: string;
  rawItemSku: string;
  workOrderId: number;
  workOrderNumber: string;
  workOrderStatus: string;
  completedQuantity: number;
  rejectQuantity: number;
  workOrderCreatedDate: string;
  workOrderBranchId: number;
  workOrderBranchName: string;
  endgoodId: number;
  endgoodName: string;
  endgoodSku: string;
  materialIssueId: number;
  issueQuantity: number;
  issueDate: string;
  materialIssueHeatNumber: string;
  workOrderOperationId: number;
  operationName: string;
  productionDate: string;
  operationCompletedQuantity: number;
  operationRejectQuantity: number;
  packingHeaderId: number;
  packingHeaderNo: string;
  packingBatchId: number;
  packingDate: string;
  packingStatus: string;
  packingDetailId: number;
  boxNumber: string;
  boxQuantity: number;
  boxStatus: string;
  isInvoiced: boolean;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  salesOrderId: number;
  salesOrderNumber: string;
  customerId: number;
  customerName: string;
  customerCode: string;
}

// DTOs for the structured output for display
export interface HeatDetails {
  heatNo: string;
  grnInfo: {
    grnId: number;
    grnNumber: string;
    grnReceivedDate: string;
    grnReceivedQuantity: number;
    grnQualityCheck: string | null;
    supplierHeatNo: string;
    tcNo: string;
    tcDate: string | null;
    rawItemName: string;
    rawItemSku: string;
  };
  associatedWorkOrders: {
    workOrderId: number;
    workOrderNumber: string;
    workOrderStatus: string;
    completedQuantity: number;
    rejectQuantity: number;
    workOrderCreatedDate: string;
    operations: {
      operationName: string;
      productionDate: string;
      operationCompletedQuantity: number;
      operationRejectQuantity: number;
    }[];
  }[];
  associatedBoxes: {
    boxNumber: string;
    boxQuantity: number;
    boxStatus: string;
    packingHeaderNo: string;
    packingDate: string;
    packingStatus: string;
    isInvoiced: boolean;
    invoiceNumber: string | null;
    invoiceDate: string | null;
    salesOrderNumber: string;
    customerName: string;
  }[];
}

export interface WorkOrderDetails {
  workOrderNumber: string;
  workOrderId: number;
  workOrderStatus: string;
  completedQuantity: number;
  rejectQuantity: number;
  workOrderCreatedDate: string;
  endgoodName: string;
  endgoodSku: string;
  associatedHeatNumbers: {
    heatNo: string;
    materialIssueDate: string;
    issueQuantity: number;
  }[];
  operations: {
    operationName: string;
    productionDate: string;
    operationCompletedQuantity: number;
    operationRejectQuantity: number;
  }[];
  associatedBoxes: {
    boxNumber: string;
    boxQuantity: number;
    boxStatus: string;
    packingHeaderNo: string;
    packingDate: string;
    isInvoiced: boolean;
    invoiceNumber: string | null;
    invoiceDate: string | null;
  }[];
}

export interface BoxDetails {
  boxNumber: string;
  boxQuantity: number;
  boxStatus: string;
  packingHeaderNo: string;
  packingDate: string;
  packingStatus: string;
  isInvoiced: boolean;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  salesOrderNumber: string;
  customerName: string;
  associatedHeatNumbers: {
    heatNo: string;
    rawItemName: string;
    materialIssueDate: string;
  }[];
  associatedWorkOrders: {
    workOrderNumber: string;
    endgoodName: string;
    completedQuantity: number;
    rejectQuantity: number;
  }[];
}

export interface ProcessedTrackingData {
  heatDetails: HeatDetails[];
  workOrderDetails: WorkOrderDetails[];
  boxDetails: BoxDetails[];
}

// Tracking API Service
class TrackingApiService extends BaseApiService {
  
  // Get tracking by heat number - returns flat array
  async getTrackingByHeatNumber(heatNo: string): Promise<RawTrackingRecord[]> {
    try {
      const data = await this.makeRequest<any>(
        API_ENDPOINTS.TRACKING.BY_HEAT_NUMBER(heatNo),
        { method: HTTP_METHODS.GET }
      );
      // Handle different response formats
      if (Array.isArray(data)) {
        return data;
      }
      // If response is wrapped in a data property
      if (data && Array.isArray(data.data)) {
        return data.data;
      }
      // Return empty array if data is not in expected format
      return [];
    } catch (error) {
      toast.error('Failed to fetch tracking data');
      throw error;
    }
  }

  // Get tracking by box number - returns flat array
  async getTrackingByBoxNumber(boxNumber: string): Promise<RawTrackingRecord[]> {
    try {
      const data = await this.makeRequest<any>(
        API_ENDPOINTS.TRACKING.BY_BOX_NUMBER(boxNumber),
        { method: HTTP_METHODS.GET }
      );
      // Handle different response formats
      if (Array.isArray(data)) {
        return data;
      }
      // If response is wrapped in a data property
      if (data && Array.isArray(data.data)) {
        return data.data;
      }
      // Return empty array if data is not in expected format
      return [];
    } catch (error) {
      toast.error('Failed to fetch tracking data');
      throw error;
    }
  }

  // Get tracking by work order number - returns flat array
  async getTrackingByWorkOrderNumber(workOrderNumber: string): Promise<RawTrackingRecord[]> {
    try {
      const data = await this.makeRequest<any>(
        API_ENDPOINTS.TRACKING.BY_WORK_ORDER_NUMBER(workOrderNumber),
        { method: HTTP_METHODS.GET }
      );
      // Handle different response formats
      if (Array.isArray(data)) {
        return data;
      }
      // If response is wrapped in a data property
      if (data && Array.isArray(data.data)) {
        return data.data;
      }
      // Return empty array if data is not in expected format
      return [];
    } catch (error) {
      toast.error('Failed to fetch tracking data');
      throw error;
    }
  }
}

export const trackingApiService = new TrackingApiService();
